import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { ActionsPalette } from "@/components/sidebar/ActionsPalette";
import {
  getKeywordKeys,
  suggestionCanBeAutocompleted,
} from "@/components/suggestions/actions/useSuggestionsActions";
import { symbolJson } from "@/components/suggestions/providers/operatorSymbolSuggestionsProvider";
import { getSuggestionNode } from "@/components/suggestions/providers/utils";
import { insertNodeFromSuggestion } from "@/components/suggestions/utils";
import { cursorIsActive } from "@/models/CursorModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { twMerge } from "@/utils/twMerge";

import {
  arrowSymbols,
  doubleStrokeSymbols,
  greekSymbols,
  operatorSymbols,
  relationOperatorSymbols,
  calculusSymbols,
  logicSymbols,
  otherSymbols,
  setSymbols,
  serifSymbols,
  circledSymbols,
  italicSymbols,
  greekItalicSymbols,
  scriptSymbols,
} from "./data";
import { symbolPaletteKeys, type SymbolPaletteItem } from "./types";

import type { SymbolItem } from "./data";
import type { Item } from "@/components/sidebar/ComboButton";
import type { SingleSuggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";

export const SymbolPaletteContent = observer(function SymbolPaletteContent({
  doc,
  item,
  id,
  tabId,
}: {
  doc: DocumentModel;
  item: SymbolPaletteItem;
  /** The tab panel's id, which its tab controls */
  id: string;
  tabId: string;
}) {
  const t = useTranslations();
  const items = useItems(item);
  const [isOpen, setIsOpen] = useState(true);

  const {
    editor: { tiptap, editorState, suggestions },
    input,
    cursor,
  } = doc;

  useEffect(() => {
    setIsOpen(true);
  }, [item.key]);

  if (items.length === 0) {
    return null;
  }

  const handleButtonClick = () => {
    setIsOpen(!isOpen);
  };

  const handleCreateSymbolNode = (symbolData: SingleSuggestion<"operatorSymbol">) => {
    doc.transact(
      () => {
        const [node] = getSuggestionNode(symbolData, doc) ?? [];

        if (!node) return;

        input.requestSmoothScroll();

        if (editorState.isExpression && cursorIsActive(cursor)) {
          const { node: addedNode } = insertNodeFromSuggestion(cursor, node);
          cursor.moveAfterNode(addedNode);
        } else if (tiptap) {
          const newInlineNode = doc.create(InlineNodeModel, [node]);

          tiptap.chain().insertInlineExpression(newInlineNode).run();

          if (node.parent) {
            cursor.moveTo(node.parent, node.parent.nodes.length);
          }
        }
      },
      "symbolPalette:insertNode",
      { forceNewUndoStack: true },
    );
  };

  const handleSelect = (i: Item) => {
    if (!tiptap) return;

    const symbol = i.icon as string;
    /**
     * TODO. There should be only one source of truth for symbols data.
     * https://linear.app/corca-research/issue/D-1833/symbol-palette-data-should-be-gathered-from-suggestion-jsons
     */
    const symbolData = symbolJson.find((s) => s.item.symbol === symbol);

    if (symbolData) {
      const context = editorState.isExpression ? "math" : "text";
      const shouldAutocomplete = suggestionCanBeAutocompleted(
        symbolData.item.symbol,
        context,
        symbolData,
      );

      if (shouldAutocomplete) {
        return handleCreateSymbolNode(symbolData);
      }
    }

    input.requestSmoothScroll();
    if (editorState.isExpression) {
      input.setValue(symbol);
      suggestions.mode = "active";

      // Added `queueMicrotask` here to make working input focusing when you select symbol from dropdown menu in symbol palette
      // see symbols in palette that has alternates like beta, delta, theta etc.
      queueMicrotask(() => {
        input.focus();
      });
    } else {
      tiptap
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .command(({ tr }) => {
          tr.insertText(symbol);

          return true;
        })
        .run();
    }
  };

  return (
    <div role="tabpanel" id={id} aria-labelledby={tabId}>
      <button
        onClick={handleButtonClick}
        className="flex w-full mt-1.25"
        type="button"
        aria-expanded={isOpen}
      >
        <Sidebar.SectionTitle className="flex">
          <Icon name="ChevronDown" className={twMerge(!isOpen && "-rotate-90")} />
          <span className="tracking-tight">{t(item.nameIntlKey)}</span>
          {isOpen && (
            <span className="flex flex-1 h-[1px] mx-1.25 my-auto bg-gray-300 dark:bg-gray-600" />
          )}
        </Sidebar.SectionTitle>
      </button>
      {isOpen && (
        <ActionsPalette
          label={t(item.nameIntlKey)}
          items={items}
          onSelect={handleSelect}
          className="p-1.25 pt-0 font-mono"
        />
      )}
    </div>
  );
});

function useItems(item: SymbolPaletteItem): (Item | Item[])[] {
  const t = useTranslations("sidebar.actionsPalette");

  switch (item.key) {
    case symbolPaletteKeys.GREEK_ALPHABET: {
      return mapSymbolsToComboItems(greekSymbols, t);
    }

    case symbolPaletteKeys.OPERATORS: {
      return mapSymbolsToComboItems(operatorSymbols, t);
    }

    case symbolPaletteKeys.RELATIONSHIP_OPERATORS: {
      return mapSymbolsToComboItems(relationOperatorSymbols, t);
    }

    case symbolPaletteKeys.CALCULUS: {
      return mapSymbolsToComboItems(calculusSymbols, t);
    }

    case symbolPaletteKeys.DOUBLE_STROKE: {
      return mapSymbolsToComboItems(doubleStrokeSymbols, t);
    }

    case symbolPaletteKeys.ARROWS: {
      return mapSymbolsToComboItems(arrowSymbols, t);
    }

    case symbolPaletteKeys.LOGIC: {
      return mapSymbolsToComboItems(logicSymbols, t);
    }

    case symbolPaletteKeys.SET: {
      return mapSymbolsToComboItems(setSymbols, t);
    }

    case symbolPaletteKeys.OTHER: {
      return mapSymbolsToComboItems(otherSymbols, t);
    }

    case symbolPaletteKeys.CIRCLED: {
      return mapSymbolsToComboItems(circledSymbols, t);
    }

    case symbolPaletteKeys.SERIF: {
      return mapSymbolsToComboItems(serifSymbols, t);
    }

    case symbolPaletteKeys.ITALIC: {
      return mapSymbolsToComboItems(italicSymbols, t);
    }

    case symbolPaletteKeys.GREEK_ITALIC: {
      return mapSymbolsToComboItems(greekItalicSymbols, t);
    }

    case symbolPaletteKeys.SCRIPT: {
      return mapSymbolsToComboItems(scriptSymbols, t);
    }

    default: {
      return [];
    }
  }
}

function mapSymbolsToComboItems(
  symbols: SymbolItem[],
  t: ReturnType<typeof useTranslations>,
  group: boolean = true,
): (Item | Item[])[] {
  const grouped = group ? groupSymbolsByGroupKey(symbols) : symbols;

  return grouped.map((item) => {
    if (Array.isArray(item)) {
      return item.map((nestedItem) => {
        const tooltipContent = getSymbolTooltipContent(nestedItem, t);
        return {
          icon: nestedItem.symbol,
          title: nestedItem.name,
          tooltipContent,
          groupIndex: nestedItem.groupIndex,
        };
      });
    }

    const tooltipContent = getSymbolTooltipContent(item, t);
    return {
      icon: item.symbol,
      title: item.name,
      tooltipContent,
      groupIndex: item.groupIndex,
    };
  });
}

function getSymbolTooltipContent(
  symbol: SymbolItem,
  t: ReturnType<typeof useTranslations>,
): string {
  if (!symbol.keywords) {
    return "";
  }
  const keywords = getKeywordKeys(symbol.keywords);
  if (keywords.length === 1) {
    return t("tooltip", { keyword: keywords[0] });
  }
  return t("tooltip2", {
    keyword: keywords[0],
    keyword2: keywords[1],
  });
}

function groupSymbolsByGroupKey(symbols: SymbolItem[]): (SymbolItem | SymbolItem[])[] {
  const groupIndexes = new Map<string, number>();

  return symbols.reduce<(SymbolItem | SymbolItem[])[]>((acc, suggestion) => {
    if (suggestion.sidebarGroupKey) {
      if (!groupIndexes.has(suggestion.sidebarGroupKey)) {
        groupIndexes.set(suggestion.sidebarGroupKey, acc.length);
        acc.push([suggestion]);
      } else {
        const groupIndex = groupIndexes.get(suggestion.sidebarGroupKey)!;
        (acc[groupIndex] as SymbolItem[]).push(suggestion);
      }
    } else {
      acc.push(suggestion);
    }

    return acc;
  }, []);
}
